import React from 'react';
import Checkbox from 'src/components/Checkbox';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import { ITableColumn } from 'src/components/Table/types';
import { getDisabledCellStyle } from 'src/utils/CommonHelpers/commonhelpers';
import { IWalkthroughModel } from 'src/services/client-types';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';

interface CreateWalkthroughTableColumnsParams {
  allSelected: boolean;
  onSelectAll: (e: any) => void;
  selectedWalkthroughIds: Set<string>;
  onWalkthroughSelect: (id: string, checked: boolean) => void;
  sendSchedulingEmail: boolean
}

export const createWalkthroughTableColumns = ({
  allSelected,
  onSelectAll,
  selectedWalkthroughIds,
  onWalkthroughSelect,
  sendSchedulingEmail
}: CreateWalkthroughTableColumnsParams): ITableColumn[] => {
  return [
    {
      label: <Checkbox checked={allSelected} value={allSelected} onChange={onSelectAll} />,
      width: '15rem',
      xsHide: false,
      cell: (data: IWalkthroughModel) => {
        const isSelected: boolean = selectedWalkthroughIds.has(data.id);
        const isDisabled: boolean = data.status !== "Activate" && !sendSchedulingEmail;
        const showPill: boolean = data.status !== "Activate";

        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            ...getDisabledCellStyle(isDisabled),
          }}>
            <Checkbox
              checked={isSelected}
              name={data.id}
              value={isSelected}
              onChange={(e: any) => onWalkthroughSelect(data.id, e.target.checked)}
              disabled={isDisabled}
            />
            {showPill && (
              <CategoryPill
                text="Already Activated"
                color="#199c3c"
                textColor="white"
                size="small"
              />
            )}
          </div>
        );
      },
    },
    {
      label: 'Name',
      accessor: 'walkthroughName',
      width: '22rem',
      sortable: true,
      cell: (data: IWalkthroughModel) => {
        const isDisabled: boolean = data.status === "Schedule";
        return (
          <div style={getDisabledCellStyle(isDisabled)}>
            {data.walkthroughName || data.title || '—'}
          </div>
        );
      }
    },
    {
      label: 'Permissions',
      accessor: 'permissions',
      width: '22rem',
      sortable: true,
      cell: (data: IWalkthroughModel) => {
        const isDisabled: boolean = data.status === "Schedule";
        return (
          <div style={getDisabledCellStyle(isDisabled)}>
            {data.permissions ? (
              <CategoryPill
                text={data.permissions}
                color={data.permissions === 'Internal' ? '#f0f0f0' : '#e8f5e9'}
                textColor={data.permissions === 'Internal' ? '#666' : '#2e7d32'}
                size="small"
              />
            ) : (
              '—'
            )}
          </div>
        );
      }
    },
    {
      label: 'Type',
      accessor: 'walkthroughType',
      width: '16rem',
      sortable: true,
      cell: (data: IWalkthroughModel) => {
        const isDisabled: boolean = data.status === "Schedule";
        return (
          <div style={getDisabledCellStyle(isDisabled)}>
            {data.walkthroughType}
          </div>
        );
      }
    },
    {
      label: 'Status',
      accessor: 'status',
      width: '16rem',
      sortable: true,
      cell: (data: IWalkthroughModel) => {
        // Todo: Quick hack to change this pill name probably should be changed at the data source.
        const isDisabled: boolean = data.status === "Schedule";
        const displayText: string = data.status === 'Activate' ? 'Pending Activation' : data.status;

        return (
          <div style={getDisabledCellStyle(isDisabled)}>
            <CategoryPill
              text={displayText}
              color={data.status === 'Activate' ? '#e3f2fd' : '#f8f9fa'}
              textColor={data.status === 'Activate' ? '#1976d2' : '#333'}
              size="small"
            />
          </div>
        );
      }
    },
    {
      label: 'Description',
      accessor: 'description',
      width: '1fr',
      sortable: false,
      textOverflow: true,
      cell: (data: any) => {
        const isDisabled: boolean = data.status === "Schedule";
        const description: string = data.description?.trim() ?? '';
        return (
          <div style={getDisabledCellStyle(isDisabled)}>
            <ExpandableText
              text={description || '—'}
              disabled={isDisabled}
              characterLimit={80}
            />
          </div>
        );
      }
    }
  ];
};